import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import MapViewSVG from '/map_view.svg';

import { extractLanguage } from '@/configuration/lang/lang';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { ClassificationGuideModal } from '@/features/dashboard/components/ClassificationGuideModal';
import { RIO_PRICE_LIST_URL } from '@/lib/constants';
import { formatUrl, t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

export const MapViewPanel = () => {
    const intl = useIntl();
    const { account } = useUserStore();
    const debugConfig = createDebugConfig();

    const [openModal, setOpenModal] = useState<boolean>(false);

    const lng = 16.36282303009959;
    const lat = 52.617114366820644;
    const zoomLevel = 5;

    const location =
        lng && lat && zoomLevel
            ? {
                  lng,
                  lat,
                  zoomLevel,
              }
            : undefined;

    const buildLinkToLiveMonitor =
        location != null
            ? `https://livemonitor.rio.cloud/#/map?lat=${location.lat}&lng=${location.lng}&z=${location.zoomLevel}&mapType=1&ml=1&cs=true&tree=pois;charging&ft=true`
            : undefined;

    const fleetUrl = account?.fleetMonitorURL ?? buildLinkToLiveMonitor;

    const handleFindStationClick = () => {
        trackCustomEvent(
            'open_fleet_monitor_link',
            {
                trigger: 'click',
                flow_name: 'overview',
                element_name: 'fleet_monitor_button',
            },
            debugConfig
        );
    };

    const handlePriceListClick = () => {
        trackCustomEvent(
            'open_price_list',
            {
                trigger: 'click',
                flow_name: 'overview',
                element_name: 'price_list_link',
            },
            debugConfig
        );
    };

    const userLanguage = extractLanguage(intl.locale);
    const priceListUrl = formatUrl(RIO_PRICE_LIST_URL, userLanguage);

    return (
        <>
            <ClassificationGuideModal open={openModal} setOpen={setOpenModal} />
            <div className='panel width-33pct' data-testid='map-view-panel'>
                <div className='display-flex flex-column align-items-center gap-20 padding-20 text-center'>
                    <div className='position-relative width-100pct min-height-250'>
                        <img
                            src={MapViewSVG}
                            alt={t('overview.findChargingStations.alternativeImageText')}
                            className='width-100pct height-100pct inset-0'
                            style={{ objectFit: 'cover' }}
                        />
                        <div
                            className='width-50 height-50 position-absolute bottom-0 left-50pct display-flex translate-x-50pct-y50pct
              align-items-center justify-conten-center rounded-circle bg-white padding-10'
                        >
                            <span className='rioglyph rioglyph-filling-e-station text-size-h2' />
                        </div>
                    </div>
                    <h3 className='margin-0' style={{ fontWeight: '700' }}>
                        <FormattedMessage id='overview.findChargingStations.title' />
                    </h3>
                    <span>
                        <FormattedMessage
                            id='overview.findChargingStations.description'
                            values={{
                                b: chunks => <b>{chunks}</b>,
                            }}
                        />
                    </span>
                    <span className='margin-x-25 text-color-dark text-size-12 margin-bottom-10'>
                        <span className='rioglyph rioglyph-info-sign text-size-16 margin-right-5' />
                        <FormattedMessage
                            id='overview.findChargingStations.info'
                            values={{
                                link: msg => (
                                    <a
                                        className='text-bold'
                                        onClick={() => setOpenModal(true)}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        {msg}
                                    </a>
                                ),
                            }}
                        />
                    </span>
                    <div className='display-flex flex-wrap-reverse justify-content-center gap-10'>
                        <Link
                            className='btn btn-default'
                            target='_blank'
                            to={priceListUrl}
                            onClick={handlePriceListClick}
                        >
                            <span className='rioglyph rioglyph-document' />
                            <span>{t('overview.button.priceList')}</span>
                        </Link>
                        {fleetUrl && (
                            <Link
                                className='btn btn-primary btn-md btn-component'
                                target='_blank'
                                to={fleetUrl}
                                onClick={handleFindStationClick}
                            >
                                <span className='rioglyph rioglyph-poi' />
                                <span>{t('general.findChargingStations')}</span>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
